import { useState, type ReactNode } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '../../../components/ui';
import type { KanbanBoard, KanbanActivity, KanbanColumn } from '../types';
import type { UITranslations } from '../i18n/translations';
import { createActivity, moveActivity } from '../services/api';

interface KanbanBoardViewProps {
  board: KanbanBoard;
  roomId: string;
  userId: string | null;
  userName: string | null;
  userEmail: string | null;
  filterMyTasks: boolean;
  t: UITranslations['room'];
  onActivityClick: (activity: KanbanActivity) => void;
  onBoardUpdate: (board: KanbanBoard) => void;
}

interface SortableActivityCardProps {
  activity: KanbanActivity;
  onClick: () => void;
}

const SortableActivityCard = ({ activity, onClick }: SortableActivityCardProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: activity.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={onClick}
      className="cursor-pointer rounded-lg border border-border/60 bg-card p-3 shadow-sm transition-shadow hover:shadow-md"
    >
      <p className="text-sm font-medium">{activity.title}</p>
      {activity.assignee && (
        <p className="mt-1 text-xs text-muted-foreground">👤 {activity.assignee}</p>
      )}
      {activity.comments.length > 0 && (
        <p className="mt-1 text-xs text-muted-foreground">💬 {activity.comments.length}</p>
      )}
    </div>
  );
};

const ActivityDragOverlay = ({ activity }: { activity: KanbanActivity }) => (
  <div className="cursor-grabbing rounded-lg border border-primary/40 bg-card p-3 shadow-xl">
    <p className="text-sm font-medium">{activity.title}</p>
    {activity.assignee && (
      <p className="mt-1 text-xs text-muted-foreground">👤 {activity.assignee}</p>
    )}
  </div>
);

const DroppableColumn = ({
  columnId,
  children,
}: {
  columnId: string;
  children: ReactNode;
}) => {
  const { setNodeRef, isOver } = useDroppable({ id: columnId });

  return (
    <div
      ref={setNodeRef}
      className={`flex min-w-[260px] max-w-[300px] flex-shrink-0 flex-col rounded-xl border bg-muted/30 p-3 ${
        isOver ? 'border-primary/40 bg-muted/50' : ''
      }`}
    >
      {children}
    </div>
  );
};

export const KanbanBoardView = ({
  board,
  roomId,
  userId,
  userName,
  userEmail,
  filterMyTasks,
  t,
  onActivityClick,
  onBoardUpdate,
}: KanbanBoardViewProps) => {
  const [activeActivity, setActiveActivity] = useState<KanbanActivity | null>(null);
  const [addingToColumn, setAddingToColumn] = useState<string | null>(null);
  const [newActivityTitle, setNewActivityTitle] = useState('');
  const [newActivityDescription, setNewActivityDescription] = useState('');
  const [addingActivity, setAddingActivity] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    })
  );

  const sortedColumns = [...board.columns].sort((a, b) => a.order - b.order);
  const firstColumnId = sortedColumns[0]?.id ?? null;

  const getColumnActivities = (columnId: string): KanbanActivity[] => {
    let activities = board.activities.filter((a) => a.columnId === columnId);
    if (filterMyTasks && userId) {
      const expectedAssignee = userEmail ?? userName ?? userId;
      activities = activities.filter((a) => a.assignee === expectedAssignee);
    }
    return activities.sort((a, b) => a.order - b.order);
  };

  const handleDragStart = (event: DragStartEvent) => {
    const activity = board.activities.find((a) => a.id === event.active.id);
    setActiveActivity(activity ?? null);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    setActiveActivity(null);
    const { active, over } = event;
    if (!over) return;

    const draggedActivity = board.activities.find((a) => a.id === active.id);
    if (!draggedActivity) return;

    // Determine target column: over could be a column or another activity
    const targetColumn = board.columns.find((c) => c.id === over.id);
    const targetActivity = board.activities.find((a) => a.id === over.id);
    const targetColumnId = targetColumn?.id ?? targetActivity?.columnId;

    if (!targetColumnId || targetColumnId === draggedActivity.columnId) return;

    const sourceIndex = sortedColumns.findIndex((c) => c.id === draggedActivity.columnId);
    const targetIndex = sortedColumns.findIndex((c) => c.id === targetColumnId);
    if (sourceIndex === -1 || targetIndex === -1) return;
    if (Math.abs(targetIndex - sourceIndex) > 1) {
      setError(t.moveNotAllowed);
      return;
    }

    try {
      const updatedBoard = await moveActivity(roomId, board.id, draggedActivity.id, {
        columnId: targetColumnId,
      });
      onBoardUpdate(updatedBoard);
    } catch (err) {
      console.error('Failed to move activity:', err);
      setError(err instanceof Error ? err.message : 'Failed to move activity');
    }
  };

  const handleAddActivity = async () => {
    if (!newActivityTitle.trim()) return;
    if (!firstColumnId) return;
    setAddingActivity(true);
    try {
      const updatedBoard = await createActivity(roomId, board.id, {
        title: newActivityTitle.trim(),
        description: newActivityDescription.trim() || undefined,
        columnId: firstColumnId,
      });
      onBoardUpdate(updatedBoard);
      setNewActivityTitle('');
      setNewActivityDescription('');
      setAddingToColumn(null);
    } catch (err) {
      console.error('Failed to create activity:', err);
      setError(err instanceof Error ? err.message : 'Failed to create activity');
    } finally {
      setAddingActivity(false);
    }
  };

  return (
    <div>
      {error && (
        <div className="mb-4 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}
      <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <div className="flex gap-4 overflow-x-auto pb-4">
          {sortedColumns.map((column: KanbanColumn) => {
            const columnActivities = getColumnActivities(column.id);
            return (
              <DroppableColumn key={column.id} columnId={column.id}>
                <div className="mb-3 flex items-center justify-between">
                  <h3 className="text-sm font-semibold">{column.name}</h3>
                  <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                    {columnActivities.length}
                  </span>
                </div>

                <SortableContext
                  items={columnActivities.map((a) => a.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="flex min-h-[60px] flex-col gap-2">
                    {columnActivities.map((activity) => (
                      <SortableActivityCard
                        key={activity.id}
                        activity={activity}
                        onClick={() => onActivityClick(activity)}
                      />
                    ))}
                  </div>
                </SortableContext>

                {addingToColumn === column.id ? (
                  <div className="mt-2 space-y-2">
                    <input
                      autoFocus
                      type="text"
                      value={newActivityTitle}
                      onChange={(e) => setNewActivityTitle(e.target.value)}
                      placeholder={t.activityTitlePlaceholder}
                      disabled={addingActivity}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleAddActivity();
                        if (e.key === 'Escape') {
                          setAddingToColumn(null);
                          setNewActivityTitle('');
                          setNewActivityDescription('');
                        }
                      }}
                      className="h-9 w-full rounded-lg border border-border/80 bg-background/80 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
                    />
                    <textarea
                      value={newActivityDescription}
                      onChange={(e) => setNewActivityDescription(e.target.value)}
                      placeholder={t.activityDescriptionPlaceholder}
                      disabled={addingActivity}
                      rows={2}
                      className="w-full resize-none rounded-lg border border-border/80 bg-background/80 px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
                    />
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        className="flex-1"
                        onClick={() => handleAddActivity()}
                        disabled={addingActivity || !newActivityTitle.trim()}
                      >
                        {t.addActivity}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setAddingToColumn(null);
                          setNewActivityTitle('');
                        }}
                      >
                        ✕
                      </Button>
                    </div>
                  </div>
                ) : (
                  column.id === firstColumnId && (
                    <button
                      onClick={() => setAddingToColumn(column.id)}
                      className="mt-2 flex w-full items-center gap-1 rounded-lg p-2 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                    >
                      <span>+</span> {t.addActivity}
                    </button>
                  )
                )}
              </DroppableColumn>
            );
          })}
        </div>

        <DragOverlay>
          {activeActivity ? <ActivityDragOverlay activity={activeActivity} /> : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
};


